/**
 * A fan's declined payment: money that was never collected.
 *
 * `payouts.status = 'payment_failed'` is written only when the fan's charge
 * never went through: by the charge guard on new orders, and by the audits of
 * 31 Aug and 14 Sep 2026 for the rows minted before the guard existed
 * (`create_payout_on_order_completion()` used to mint a payout without checking
 * the charge). None of that money was collected and none of it is owed, so it
 * has no place on a creator's screen: not in a list, not in a total, not in a
 * count.
 *
 * It is NOT `failed`. A `failed` payout is a bank transfer TO the creator that
 * bounced; that money is still owed and stays visible. So does a genuine refund
 * (`is_refunded` on a charge that did go through), struck through as before.
 *
 * Every creator-facing read of `get-talent-payouts` goes through
 * `scrubDeclinedCharges` (creatorApi's `getTalentPayouts`, and the classic
 * dashboard's own call). Admin reads the tables directly and is unchanged.
 */

export const DECLINED_CHARGE_STATUS = 'payment_failed';

type StatusRow = { status?: string | null } | null | undefined;

export function isDeclinedCharge(row: StatusRow): boolean {
  return String(row?.status ?? '').trim().toLowerCase() === DECLINED_CHARGE_STATUS;
}

const num = (value: unknown) => Number(value) || 0;
const cents = (value: number) => Math.round(value * 100);

type PayoutLike = Record<string, any> & {
  status?: string | null;
  payout_type?: string | null;
  week_start_date?: string | null;
  batch_id?: string | null;
  payout_amount?: number | string | null;
  is_refunded?: boolean | null;
};

type BatchLike = Record<string, any> & {
  id: string;
  kind?: string | null;
  status?: string | null;
  week_start_date?: string | null;
  total_orders?: number | string | null;
  total_payout_amount?: number | string | null;
  total_refunded_amount?: number | string | null;
  net_payout_amount?: number | string | null;
};

const SENT = new Set(['paid', 'complete', 'completed', 'processing']);

/**
 * A weekly batch built before the charge guard counted every payout row of its
 * week, so a few still carry a declined charge inside `total_orders` and the
 * amounts. Take it back out for display, but only when the batch's own numbers
 * prove the declined rows are in it: its order count AND its gross equal the
 * week's rows with them included. A batch built after the guard, or corrected
 * by hand, doesn't match and is left exactly as stored, so nothing is ever
 * taken out twice. Only money not yet sent is corrected.
 *
 * Returns null when nothing but declined charges was in the batch.
 */
export function batchWithoutDeclined<B extends BatchLike>(
  batch: B,
  payouts: PayoutLike[]
): B | null {
  if (batch.kind === 'ad_revenue') return batch;
  // Once a transfer has gone (or is going) out, its amount is what reached the
  // bank, and the screen has to agree with the bank statement.
  if (SENT.has(String(batch.status ?? '').trim().toLowerCase())) return batch;

  // The same rows the payout runner sweeps into a weekly batch: this week's
  // non-ad rows not already owned by a different batch.
  const weekRows = payouts.filter(
    (p) =>
      p.payout_type !== 'ad_revenue' &&
      p.week_start_date === batch.week_start_date &&
      (!p.batch_id || p.batch_id === batch.id)
  );
  const declined = weekRows.filter(isDeclinedCharge);
  if (declined.length === 0) return batch;

  const orders = num(batch.total_orders);
  const gross = num(batch.total_payout_amount);
  const weekGross = weekRows.reduce((sum, p) => sum + num(p.payout_amount), 0);
  if (orders !== weekRows.length || cents(gross) !== cents(weekGross)) return batch;

  const declinedGross = declined.reduce((sum, p) => sum + num(p.payout_amount), 0);
  const declinedRefunded = declined
    .filter((p) => p.is_refunded)
    .reduce((sum, p) => sum + num(p.payout_amount), 0);

  // A declined row the batch already netted out as a refund is out of the net;
  // the rest of it is still inside the net.
  const refunded = num(batch.total_refunded_amount);
  const refundTakenBack = Math.min(refunded, declinedRefunded);
  const netTakenBack = declinedGross - refundTakenBack;

  const remainingOrders = orders - declined.length;
  if (remainingOrders <= 0) return null;

  return {
    ...batch,
    total_orders: remainingOrders,
    total_payout_amount: cents(gross - declinedGross) / 100,
    total_refunded_amount: cents(refunded - refundTakenBack) / 100,
    net_payout_amount: Math.max(0, cents(num(batch.net_payout_amount) - netTakenBack) / 100),
  };
}

export type ScrubbedPayouts<P extends PayoutLike, B extends BatchLike> = {
  payouts: P[];
  batches: B[];
  /** Every non-refunded payout, declined charges left out. Dollars. */
  totalEarnings: number;
  /** Orders whose fan payment was declined, for screens that count orders. */
  declinedOrderIds: string[];
};

/**
 * The `get-talent-payouts` payload with every declined charge taken out.
 *
 * `totalEarnings` is summed again from the remaining rows with the endpoint's
 * own rule (every row that isn't refunded), rather than by subtracting from the
 * endpoint's figure, so it stays right if the endpoint ever learns to leave
 * declined charges out itself.
 */
export function scrubDeclinedCharges<P extends PayoutLike, B extends BatchLike>(
  raw: { payouts?: P[] | null; batches?: B[] | null } & Record<string, any>
): ScrubbedPayouts<P, B> & Record<string, any> {
  const all = (raw?.payouts ?? []) as P[];
  const payouts = all.filter((p) => !isDeclinedCharge(p));
  const batches = ((raw?.batches ?? []) as B[])
    .map((b) => batchWithoutDeclined(b, all))
    .filter((b): b is B => b !== null);
  const kept = payouts.filter((p) => !p.is_refunded);
  const totalEarnings = cents(kept.reduce((sum, p) => sum + num(p.payout_amount), 0)) / 100;
  const declinedOrderIds = all
    .filter((p) => isDeclinedCharge(p) && p.order_id)
    .map((p) => String(p.order_id));

  return { ...raw, payouts, batches, totalEarnings, declinedOrderIds };
}
